'use client';

import React, { useEffect, useRef, useState, useCallback, ReactNode, ComponentType } from 'react';
import {
  commonBusinessLogger,
  COMMON_EVENT_NAMES,
  PageName,
} from '@/utils/logger/instance/commonBusinessLogger';
// 监控配置类型
export interface MonitorConfig {
  name: string; // 组件名称（必填）
  shouldShow: () => boolean; // 渲染条件（必填）
  delay?: number; // 检查延迟(ms)，默认100
  enabled?: boolean; // 是否启用，默认true
  logData?: {
    page: PageName;
    message?: Record<string, string | number | boolean>;
  }; // 额外日志数据
}

// 判断是否为客户端环境
const isClient = typeof window !== 'undefined';

// 核心监控Hook
export function useComponentMonitor(config: MonitorConfig) {
  const { name, shouldShow, delay = 100, enabled = true, logData } = config;
  const ref = useRef<HTMLDivElement>(null);
  const [domReady, setDomReady] = useState(false);

  // 监听DOM就绪状态，确保在DOM完全加载后才开始组件监控
  useEffect(() => {
    // 仅在客户端环境执行，避免SSR时访问window对象报错
    if (!isClient) return;

    // DOM就绪处理函数：将domReady状态设置为true
    const handleReady = () => setDomReady(true);

    // 检查当前DOM加载状态
    // document.readyState有三种状态：'loading'、'interactive'、'complete'
    if (document.readyState !== 'loading') {
      // DOM已经加载完成（interactive或complete状态），立即执行处理函数
      handleReady();
    } else {
      // DOM仍在加载中，添加DOMContentLoaded事件监听器等待加载完成
      window.addEventListener('DOMContentLoaded', handleReady);
    }

    // 清理函数：组件卸载时移除事件监听器，防止内存泄漏
    // 必须传入与addEventListener相同的函数引用才能正确移除
    return () => window.removeEventListener('DOMContentLoaded', handleReady);
  }, []); // 空依赖数组确保只在组件挂载和卸载时执行

  // 稳定渲染条件函数引用
  const stableShouldShow = useCallback(shouldShow, [shouldShow]);

  // 监控逻辑
  useEffect(() => {
    if (!isClient || !domReady || !enabled) return;

    const timer = setTimeout(() => {
      const shouldRender = stableShouldShow();
      const element = ref.current;
      const hasContent = element?.children.length && element.offsetHeight > 0;

      // 异常情况日志
      if (shouldRender && (!element || !hasContent)) {
        commonBusinessLogger({
          eventName: COMMON_EVENT_NAMES.COMPONENT_MISS_RENDER,
          category: {
            page: logData?.page || '',
          },
          message: {
            info: `${name}组件应渲染但未展示`,
            component: name,
            shouldRender,
            ...logData?.message,
          },
        });
        console.error(`${name}组件应渲染但未展示`);
      } else if (!shouldRender && element && hasContent) {
        commonBusinessLogger({
          eventName: COMMON_EVENT_NAMES.COMPONENT_UNEXPECTED_RENDER,
          category: {
            page: logData?.page || '',
          },
          message: {
            info: `${name}组件不应渲染但展示`,
            component: name,
            shouldRender,
            ...logData?.message,
          },
        });
        console.error(`${name}组件不应渲染但展示`);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [name, stableShouldShow, delay, enabled, domReady, logData]);

  return ref;
}

// 监控包装组件（简化使用）
export function MonitorWrapper({
  config,
  children,
  fallback = null,
}: {
  config: MonitorConfig;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const ref = useComponentMonitor(config);
  return config.shouldShow() ? <div ref={ref}>{children}</div> : fallback;
}

// 组件类型监控（支持传组件和props）
export function MonitorComponent<T extends Record<string, any> = {}>({
  config,
  component: Component,
  props,
  fallback = null,
}: {
  config: MonitorConfig;
  component: ComponentType<T>;
  props?: T;
  fallback?: ReactNode;
}) {
  const ref = useComponentMonitor(config);
  return config.shouldShow() ? (
    <div ref={ref}>
      <Component {...(props as T)} />
    </div>
  ) : (
    fallback
  );
}

/**
 * 组件监控系统 - 用于监控组件渲染状态和日志记录
 *
 * 使用示例：
 *
 * 1. 基础Hook使用：
 * ```tsx
 * import { useComponentMonitor } from '@/hooks/componentMonitor/clientMonitor';
 *
 * function MyComponent() {
 *   const ref = useComponentMonitor({
 *     name: 'MyComponent',
 *     shouldShow: () => true,
 *     logData: { page: 'home' }
 *   });
 *
 *   return <div ref={ref}>内容</div>;
 * }
 * ```
 *
 * 2. MonitorWrapper使用：
 * ```tsx
 * import { MonitorWrapper } from '@/hooks/componentMonitor/clientMonitor';
 *
 * <MonitorWrapper
 *   config={{
 *     name: 'UserProfile',
 *     shouldShow: () => !!user,
 *     logData: { userId: user?.id }
 *   }}
 *   fallback={<div>加载中...</div>}
 * >
 *   <UserProfileCard user={user} />
 * </MonitorWrapper>
 * ```
 *
 * 3. MonitorComponent使用：
 * ```tsx
 * import { MonitorComponent } from '@/hooks/componentMonitor/clientMonitor';
 *
 * <MonitorComponent
 *   config={{
 *     name: 'ProductCard',
 *     shouldShow: () => !!product,
 *     delay: 200,
 *     logData: { productId: product?.id }
 *   }}
 *   component={ProductCard}
 *   props={{ product, onAddToCart }}
 *   fallback={<ProductSkeleton />}
 * />
 * ```
 */
